// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
import React from 'react'; /* eslint-disable-line no-unused-vars */

import { Price } from '@zapier/billing';
import { createKeyHandler } from '@zapier/common-utils';
import { Border, Colors } from '@zapier/theme';
import { RadioButton, Tag } from '@zapier/common-components';
import { css, jsx } from '@emotion/core';

/** @jsx jsx */

type Props = {
  enticement?: string,
  isCompact?: boolean,
  isDisabled?: boolean,
  isMetered?: boolean,
  isOnly?: boolean,
  isSelected?: boolean,
  name: string,
  onClick: () => void,
  price: number,
};

const billingOptionCss = (props: Props) =>
  css(
    {
      alignItems: 'center',
      border: `${Border.size.default} solid ${Border.color.default}`,
      borderRadius: Border.radius.default,
      cursor: 'pointer',
      display: 'flex',
      flexBasis: '48%',
      flexDirection: 'row',
      margin: 0,
      padding: 15,

      '@media (max-width: 750px)': {
        width: '100%',
        margin: '0 0 20px 0',

        '&:last-of-type': {
          marginBottom: 0,
        },
      },
    },
    props.isCompact && {
      flex: '5 1 0',

      '&:not(:first-of-type)': {
        flex: '3 1 0',
      },
    },
    props.isDisabled && {
      cursor: 'default',
      opacity: 0.5,
    },
    props.isOnly && {
      display: 'block',
      cursor: 'default',
      flexBasis: '100%',
      textAlign: 'center',
    },
    props.isSelected && {
      ':not(:only-of-type)': {
        borderColor: Colors.heroine,
      },
    }
  );

const descriptionCss = (props: Props) => ({
  textAlign: props.isOnly ? 'center' : 'left',
  marginLeft: props.isOnly ? 0 : 20,
});

const nameCss = {
  color: Colors.deepSpace,
  fontSize: '1.2em',
  fontWeight: 600,
  marginBottom: 10,
  textTransform: 'capitalize',
};

const priceCss = {
  fontSize: '1.1em',
};

const BillingOption = (props: Props) => {
  const {
    enticement,
    isDisabled = false,
    isMetered = false,
    isOnly = false,
    isSelected = false,
    name,
    onClick,
    price,
  } = props;

  return (
    <div
      aria-checked={isSelected}
      aria-disabled={isDisabled}
      css={billingOptionCss(props)}
      disabled={isDisabled}
      onClick={isDisabled ? null : onClick}
      onKeyDown={isDisabled ? null : createKeyHandler(['space'], onClick)}
      role="radio"
      tabIndex={0}
    >
      {!isOnly && (
        <RadioButton
          aria-label={name}
          checked={isSelected}
          className="checker"
          name="billing-option"
          value={String(price)}
        />
      )}
      <div css={descriptionCss(props)}>
        <div css={nameCss}>
          {name}{' '}
          {enticement && (
            <Tag backgroundColor="heroine" isPill={true} margin={{ left: 8 }}>
              {enticement}
            </Tag>
          )}
        </div>
        <div css={priceCss}>
          {isMetered ? 'Starts at ' : ''}
          <Price>{price}</Price> per month
        </div>
      </div>
    </div>
  );
};

export default BillingOption;
